import React, {lazy, Suspense, useEffect, useState} from "react";
/** @jsx jsx */
import { jsx } from "@emotion/core";
import { Router, Route, useLocation, Redirect, Switch } from "react-router-dom";
import { createBrowserHistory } from "history";
import routes from "./routes";
import { Layout, Row, Col } from "antd";
import { Navigation } from "../components";
import globalColors from "../styles/globalColors";
import { SecureRoute, Security } from "@okta/okta-react";
import {OktaAuth, toRelativeUrl} from "@okta/okta-auth-js";
import config from "../config";
import AuthCallback from "../pages/AuthCallback";

export const history = createBrowserHistory();

const Customers = lazy(() => import("../pages/Customer/Customers"));
const Customer = lazy(() => import("../pages/Customer/Customer"));
const Dashboard = lazy(() => import("../pages/Dashboard/Dashboard"));
const RecentActivity = lazy(() =>
  import("../pages/RecentActivity/RecentActivity")
);
const ProductSelection = lazy(() => import("../pages/Quote/ProductSelection"));
const AutoQuickPolicyGeneral = lazy(() =>
  import("../pages/Quote/Auto/AutoQuickPolicyGeneral")
);
const AutoQuickDriverDetail = lazy(() =>
  import("../pages/Quote/Auto/AutoQuickDriverDetail")
);
const AutoQuickVehicleDetail = lazy(() =>
  import("../pages/Quote/Auto/AutoQuickVehicleDetail")
);
const AutoQuickEstimatedPremium = lazy(() =>
  import("../pages/Quote/Auto/AutoQuickEstimatedPremium")
);
const AutoPolicyGeneral = lazy(() =>
  import("../pages/Quote/Auto/AutoFullPolicyGeneral")
);
const AutoQualificationQuestions = lazy(() =>
  import("../pages/Quote/Auto/AutoFullQuestions")
);
const AutoDriverDetail = lazy(() =>
  import("../pages/Quote/Auto/AutoFullDriverDetail")
);
const AutoVehicleDetail = lazy(() =>
  import("../pages/Quote/Auto/AutoFullVehicleDetail")
);
const AutoPaymentPlan = lazy(() =>
  import("../pages/Quote/Auto/AutoFullPaymentPlan")
);
const AutoConfirmation = lazy(() =>
  import("../pages/Quote/Shared/QuoteConfirmation")
);
const HomeownerPolicyGeneral = lazy(() =>
  import("../pages/Quote/Homeowner/HomeownerPolicyGeneral")
);
const HomeownerQuickPolicyGeneral = lazy(() =>
  import("../pages/Quote/Homeowner/HomeownerQuickPolicyGeneral")
);
const HomeownerQuickCoverages = lazy(() =>
  import("../pages/Quote/Homeowner/HomeownerQuickCoverages")
);
const HomeownerQuickEstimatedPremium = lazy(() =>
  import("../pages/Quote/Homeowner/HomeownerQuickEstimatedPremium")
);
const HomeownerQualificationQuestions = lazy(() =>
  import("../pages/Quote/Homeowner/HomeownerQualificationQuestions")
);
const HomeownerPropertyDetails = lazy(() =>
  import("../pages/Quote/Homeowner/HomeownerPropertyDetails")
);
const HomeownerDetail = lazy(() =>
  import("../pages/Quote/Homeowner/HomeownersDetail")
);
const HomeownerAdditionalInterests = lazy(() =>
  import("../pages/Quote/Homeowner/HomeownerAdditionalInterests")
);
const HomeownerPaymentPlan = lazy(() =>
  import("../pages/Quote/Homeowner/HomeownerFullPaymentPlan")
);
const HomeownerConfirmation = lazy(() =>
  import("../pages/Quote/Shared/QuoteConfirmation")
);
const InsuredInformation = lazy(() =>
  import("../pages/Quote/Shared/InsuredInformation")
);
const PolicyDetails = lazy(() => import("../pages/Policy/PolicyDetails"));
const Applications = lazy(() => import("../pages/Application/Applications"));
const ApplicationDetails = lazy(() =>
  import("../pages/Application/ApplicationDetails")
);
const SearchResults = lazy(() => import("../pages/SearchResults"));
const Login = lazy(() => import("../pages/Login"));
const Policies = lazy(() => import("../pages/Policy/Policies"));
const Tasks = lazy(() => import("../pages/Tasks/Tasks"));

const useScrollToTop = () => {
  let location = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location]);
};

const ScrollToTop = ({ children }: { children: any }) => {
  useScrollToTop();
  return children || null;
};

const Loadable = (Component: any) => (props: any) => (
  <Suspense fallback={<div />}>
    <Component {...props} />
  </Suspense>
);
const oktaAuth = new OktaAuth(config.oidc);

const Routes: React.FC<{}> = () => {
  function selectMenu(route: string) {
    history.push(route);
  }

// tslint:disable-next-line:variable-name
  const restoreOriginalUri = async (_oktaAuth: any, originalUri: any) => {
    history.replace(toRelativeUrl(originalUri || '/', window.location.origin));
  };

  function getSecureRoutes() {
    return (
      <div>
        <SecureRoute
            exact
            path={routes.dashboard}
            component={Loadable(Dashboard)}
        />
        <SecureRoute
            exact
            path={routes.recentActivity}
            component={Loadable(RecentActivity)}
        />
        <SecureRoute
            exact
            path={routes.customers}
            component={Loadable(Customers)}
        />
        <SecureRoute
            path={routes.customer}
            component={Loadable(Customer)}
        />
        <SecureRoute
            exact
            path={routes.tasks}
            component={Loadable(Tasks)}
        />
        <SecureRoute
            exact
            path={routes.policies.list}
            component={Loadable(Policies)}
        />
        <SecureRoute
            path={routes.policies.show}
            component={Loadable(PolicyDetails)}
        />
        <SecureRoute
            path={routes.quotes.productSelection}
            component={Loadable(ProductSelection)}
        />
        <SecureRoute
            path={routes.quotes.auto.quick.policyGeneral}
            component={Loadable(AutoQuickPolicyGeneral)}
        />
        <SecureRoute
            path={routes.quotes.auto.quick.driverDetail}
            component={Loadable(AutoQuickDriverDetail)}
        />
        <SecureRoute
            path={routes.quotes.auto.quick.vehicleDetail}
            component={Loadable(AutoQuickVehicleDetail)}
        />
        <SecureRoute
            path={routes.quotes.auto.quick.estimatedPremium}
            component={Loadable(AutoQuickEstimatedPremium)}
        />
        <SecureRoute
            path={routes.quotes.auto.policyGeneral}
            component={Loadable(AutoPolicyGeneral)}
        />
        <SecureRoute
            path={routes.quotes.auto.insuredInformation}
            component={Loadable(InsuredInformation)}
        />
        <SecureRoute
            path={routes.quotes.auto.qualificationQuestions}
            component={Loadable(AutoQualificationQuestions)}
        />
        <SecureRoute
            path={routes.quotes.auto.driverDetail}
            component={Loadable(AutoDriverDetail)}
        />
        <SecureRoute
            path={routes.quotes.auto.vehicleDetail}
            component={Loadable(AutoVehicleDetail)}
        />
        <SecureRoute
            path={routes.quotes.auto.paymentPlans}
            component={Loadable(AutoPaymentPlan)}
        />
        <SecureRoute
            path={routes.quotes.auto.confirmation}
            component={Loadable(AutoConfirmation)}
        />
        <SecureRoute
            path={routes.quotes.homeowner.quick.policyGeneral}
            component={Loadable(HomeownerQuickPolicyGeneral)}
        />
        <SecureRoute
            path={routes.quotes.homeowner.quick.coverage}
            component={Loadable(HomeownerQuickCoverages)}
        />
        <SecureRoute
            path={routes.quotes.homeowner.quick.estimate}
            component={Loadable(HomeownerQuickEstimatedPremium)}
        />
        <SecureRoute
            path={routes.quotes.homeowner.policyGeneral}
            component={Loadable(HomeownerPolicyGeneral)}
        />
        <SecureRoute
            path={routes.quotes.homeowner.qualificationQuestions}
            component={Loadable(HomeownerQualificationQuestions)}
        />
        <SecureRoute
            path={routes.quotes.homeowner.propertyDetails}
            component={Loadable(HomeownerPropertyDetails)}
        />
        <SecureRoute
            path={routes.quotes.homeowner.detail}
            component={Loadable(HomeownerDetail)}
        />
        <SecureRoute
            path={routes.quotes.homeowner.additionalInterests}
            component={Loadable(HomeownerAdditionalInterests)}
        />
        <SecureRoute
            path={routes.quotes.homeowner.paymentPlans}
            component={Loadable(HomeownerPaymentPlan)}
        />
        <SecureRoute
            path={routes.quotes.homeowner.confirmation}
            component={Loadable(HomeownerConfirmation)}
        />
        <SecureRoute
            path={routes.quotes.homeowner.insuredInformation}
            component={Loadable(InsuredInformation)}
        />
        <SecureRoute
            exact
            path={routes.applications.list}
            component={Loadable(Applications)}
        />
        <SecureRoute
            path={routes.applications.show}
            component={Loadable(ApplicationDetails)}
        />
        <SecureRoute
            path={routes.search}
            component={Loadable(SearchResults)}
        />
      </div>
    )
  }

  return (
      <Security oktaAuth={oktaAuth} restoreOriginalUri={restoreOriginalUri}>
      <Router history={history}>
        <Layout css={styles.layout}>
          <Navigation selectMenu={selectMenu} />
          <Layout.Content css={styles.content}>
            <Row css={styles.container}>
              <Col span={18} offset={3}>
                <ScrollToTop>
                  <Switch>
                    <Route
                      exact
                      path={routes.login}
                      component={Loadable(Login)}
                    />
                    <Route
                      exact
                      path={routes.authCallback}
                      component={Loadable(AuthCallback)}
                    />
                    {getSecureRoutes()}
                    <Redirect to={routes.login} />
                  </Switch>
                </ScrollToTop>
              </Col>
            </Row>
          </Layout.Content>
        </Layout>
      </Router>
    </Security>
  );
};

const styles = {
  layout: {
    background: globalColors.white
  },
  content: {
    padding: "74px 0 0",
    background: globalColors.white,
    height: "100vh"
  },
  container: { padding: "30px 0 0" }
};

export default Routes;
